import { useSelector } from 'react-redux';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { axFetch } from '/@/store/session/actions';
import { getEnableRvCustomHome, getUser } from '/@/store/session/selectors';
import type { User } from '/@/types/core';
import {
    isUserValidatedAdvisor,
    isUserValidatedConsultant,
    isUserValidatedRV,
    isUserValidatedSquad,
    isValidatedBayerUserType,
} from '/@/utils';

export const getLocationsFromApi = async ({
    level,
    page,
    pageSize,
    parentIdIn,
    parentId,
}: {
    level: number;
    page?: number;
    pageSize?: number;
    parentIdIn?: number[];
    parentId?: number;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/zones/locations/`, window.location.origin);
    const res = await axFetch.get(apiUrl.toString(), {
        params: {
            page_size: pageSize,
            page,
            level,
            parent_id__in: parentIdIn?.join(','),
            parent_id: parentId,
        },
    });
    return res.data;
};

export const useLocations = (
    {
        level = 0,
        page,
        pageSize = 10,
        parentIdIn,
        parentId,
    }: {
        level: number;
        page?: number;
        pageSize?: number;
        parentIdIn?: number[];
        parentId?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) =>
    useSWRImmutable(
        enabled ? { key: 'getLocationsFromApi', page, pageSize, level, parentIdIn, parentId } : null,
        getLocationsFromApi
    );

export const useIsValidatedRV = () => {
    const user = useSelector(getUser);
    const enableRvCustomHome = useSelector(getEnableRvCustomHome);
    return enableRvCustomHome && isUserValidatedRV(user as User);
};

export const useIsValidatedAdvisor = () => {
    const user = useSelector(getUser);
    const enableRvCustomHome = useSelector(getEnableRvCustomHome);
    return enableRvCustomHome && isUserValidatedAdvisor(user as User);
};
export const useIsValidatedSquad = () => {
    const user = useSelector(getUser);
    const enableRvCustomHome = useSelector(getEnableRvCustomHome);
    return enableRvCustomHome && isUserValidatedSquad(user as User);
};

export const USER_TYPES = ['rv', 'consultant', 'squad_coach', 'grower'] as const;
export type UserType = (typeof USER_TYPES)[number];

export const useValidatedType = () => {
    const isValidatedRV = useIsValidatedRV();
    const isValidatedConsultant = useIsValidatedConsultant();
    const isValidatedSquad = useIsValidatedSquad();
    const isValidatedAdvisor = useIsValidatedAdvisor();

    if (isValidatedSquad) {
        return 'squad_coach';
    }
    if (isValidatedRV) {
        return 'rv';
    }
    if (isValidatedConsultant) {
        return 'consultant';
    }
    if (isValidatedAdvisor) {
        return 'advisor';
    }
    return 'grower';
};

export const useIsValidatedConsultant = () => {
    const user = useSelector(getUser);
    const enableRvCustomHome = useSelector(getEnableRvCustomHome);
    return enableRvCustomHome && isUserValidatedConsultant(user as User);
};

export const useHasWriteAccess = () => {
    const isUserValidatedSquad = useIsValidatedSquad();
    return !isUserValidatedSquad;
};

export const useIsValidatedBayerUserType = () => {
    const user = useSelector(getUser);
    const enableRvCustomHome = useSelector(getEnableRvCustomHome);
    return enableRvCustomHome && isValidatedBayerUserType(user as User);
};
